import React, { Suspense, useEffect, useState } from "react";
import { Box, Spinner, Center, Text } from "@chakra-ui/react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useUser, SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { identifyUser, track, capturePageview, Events } from './utils/analytics';
import { retryLazy } from './utils/chunkLoader';
import { useSessionStore } from "./store";
// Eager imports: small, always-rendered components and hooks
import { useAuthenticationManager } from './utils/authWrapper';
import LegacyRoomRedirect from './Components/LegacyRoomRedirect';
import { useOnboardingSurvey } from './hooks/useOnboardingSurvey';
import OnboardingSurveyModal from './Components/OnboardingSurveyModal';
import EnvironmentBanner from "./Components/EnvironmentBanner";
import SafariBarTint from "./Components/SafariBarTint";
import ChunkErrorBoundary from './Components/ChunkErrorBoundary';
// Editor panels — eagerly imported since they're used in route definitions
import RoomControlPanel from "./Components/ObjectPanel/RoomControlPanel";
import RoomDecorationsPanel from "./Components/ObjectPanel/RoomDecorationsPanel";
import RoomSelectionPanel from "./Components/ObjectPanel/RoomSelectionPanel";
import RoomSettingsPanel from "./Components/ObjectPanel/RoomSettingsPanel";

// Page-level CSS eagerly imported to prevent CRA from extracting them into
// separate CSS chunks when pages are lazy-loaded. Without this, a missing
// CSS chunk file causes ChunkLoadError and breaks the UI.
import './styles/Landing/Landing.css';
import './styles/InfoPage.css';
import './styles/Account.css';
import './styles/LegalPage.css';

// Route-level code splitting: each page loads only when its route is visited.
// Wrapped with retryLazy to retry failed chunk loads (3 attempts) before
// falling through to ChunkErrorBoundary.
const HomePage = retryLazy(() => import("./pages/HomePage"));
const EditorPage = retryLazy(() => import("./pages/EditorPage"));
const PlayerPage = retryLazy(() => import("./pages/PlayerPage"));
const AdminPage = retryLazy(() => import("./pages/AdminPage"));
const SignupPage = retryLazy(() => import('./pages/SignupPage'));
const SignInPage = retryLazy(() => import('./pages/SignInPage'));
const ProfilePage = retryLazy(() => import('./pages/ProfilePage'));
const CollectionPage = retryLazy(() => import('./pages/CollectionPage'));
const LandingPage = retryLazy(() => import("./pages/LandingPage"));
const AllRoomsPage = retryLazy(() => import("./pages/AllRoomsPage"));
const TermsOfServicePage = retryLazy(() => import("./pages/TermsOfServicePage"));
const PrivacyPolicyPage = retryLazy(() => import("./pages/PrivacyPolicyPage"));
const FAQPage = retryLazy(() => import("./pages/FAQPage"));
const AnnouncementsPage = retryLazy(() => import("./pages/AnnouncementsPage"));
const ErrorTestPage = retryLazy(() => import('./pages/ErrorTestPage'));
const CreatorProfilePage = retryLazy(() => import('./pages/CreatorProfilePage'));
const AvatarCreatorPage = retryLazy(() => import('./pages/AvatarCreatorPage'));

// Suspense fallback for route-level loading. Matches the light loader used on
// Home/Editor/Player (bg gray.100 + default xl spinner) so the chunk-download
// gap doesn't flash a dark screen before the page's own loader takes over.
const PageLoader = () => (
  <Center h="100vh" bg="gray.100">
    <Box textAlign="center">
      <Spinner size="xl" mb={4} />
      <Text>Loading...</Text>
    </Box>
  </Center>
);



function App() {
  // Handle authentication and guest-to-user transitions
  useAuthenticationManager();

  const { isLoaded, isSignedIn, user } = useUser();
  const { shouldShowSurvey, dismissSurvey } = useOnboardingSurvey();
  const location = useLocation();
  const [wasSignedIn, setWasSignedIn] = useState(false);

  // Capture pageview on every route change
  useEffect(() => {
    capturePageview();
  }, [location.pathname, capturePageview]);

  // Track auth completion (sign-up vs sign-in) and identify users
  useEffect(() => {
    if (isLoaded && isSignedIn && user) {
      const authIntent = window.__AUTH_INTENT__;
      const isNewUser = authIntent === 'signup';

      if (!wasSignedIn) {
        track(Events.SIGN_IN_COMPLETED, {
          method: 'clerk',
          isNewUser: Boolean(isNewUser),
        });
      }

      identifyUser(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
      });

      // Clear the auth intent flag after use
      window.__AUTH_INTENT__ = null;
    }
    setWasSignedIn(isSignedIn);
  }, [isLoaded, isSignedIn, user?.id, wasSignedIn, track, Events, identifyUser, capturePageview]);

  // Initialize session ID on app start
  useEffect(() => {
    useSessionStore.getState().getOrCreateSessionId();
    // Always reset migration flag on startup — prevents a stale persisted true from blocking EditorPage
    useSessionStore.getState().setIsMigrating(false);
  }, []);


  return (
    <>
      <EnvironmentBanner />
      {/* Dark iOS 26 Safari top/bottom bar tint, mounted globally on every page
          (theme-color is ignored on iOS 26). See Components/SafariBarTint.js. */}
      <SafariBarTint />
      <Box>
        <ChunkErrorBoundary>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/announcements" element={<AnnouncementsPage />} />
            <Route path="/create" element={<HomePage/>} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/collection" element={<CollectionPage />} />
            <Route path="/shop" element={<Navigate to="/collection" replace />} />
            {/* Redirect legacy routes to profile page */}
            <Route path="/account" element={<ProfilePage />} />
            <Route path="/my-rooms" element={<ProfilePage />} />
            <Route path="/rooms" element={<AllRoomsPage />} />
            <Route path="/signup/:roomId/*" element={<SignupPage />} />
          <Route path="/sign-in/:roomId/*" element={<SignInPage />} />

          {/* Room selection step - supports both roomId and slug */}
          <Route
            path="/editor/room-selection/:roomIdentifier"
            element={
              <EditorPage
                keyForPanel={"step1"}
                panels={[
                  {
                    label: "Select Room Size:",
                    value: "size",
                    component: RoomSelectionPanel,
                    isOpen: true,
                  },
                ]}
              />
            }
          />
          {/* Step 2 - Wall Type */}
          <Route
            path="/editor/room-walls/:roomId"
            element={
              <EditorPage
                keyForPanel={"step2"}
                panels={[
                  {
                    label: "Wall Type",
                    value: "walltype",
                    component: RoomControlPanel,
                    isOpen: true,
                  },
                ]}
              />
            }
          />
          {/* Step 2b - Decorations */}
          <Route
            path="/editor/room-decorations/:roomId"
            element={
              <EditorPage
                keyForPanel={"step2b"}
                panels={[
                  {
                    label: "Images and Links",
                    value: "decoration",
                    component: RoomDecorationsPanel,
                    isOpen: true,
                  },
                ]}
              />
            }
          />
          {/* Step 4 - Room Settings (publish step) - must be before :roomIdentifier wildcard */}
          <Route
            path="/editor/room-settings/:roomId"
            element={
              <EditorPage
                keyForPanel={"step4"}
                panels={[
                  {
                    label: "Final Touches",
                    value: "settings",
                    component: RoomSettingsPanel,
                    isOpen: true,
                  },
                ]}
              />
            }
          />
          {/* Step 3 - 3D Asset Placement (wildcard catches remaining /editor/* paths) */}
          <Route path="/editor/:roomIdentifier" element={<EditorPage keyForPanel={"step3"} />} />
          {/* Player route */}
          <Route path="/player/:roomIdentifier" element={<PlayerPage />} />

          {/* Legacy static rooms - redirect to /roomname/index.html */}
          <Route path="/1" element={<LegacyRoomRedirect />} />
          <Route path="/2025" element={<LegacyRoomRedirect />} />
          <Route path="/ambientplex1" element={<LegacyRoomRedirect />} />
          <Route path="/anthony1" element={<LegacyRoomRedirect />} />
          <Route path="/basketball" element={<LegacyRoomRedirect />} />
          <Route path="/cinema" element={<LegacyRoomRedirect />} />
          <Route path="/danteraddd" element={<LegacyRoomRedirect />} />
          <Route path="/djvektorb" element={<LegacyRoomRedirect />} />
          <Route path="/downthetubes0" element={<LegacyRoomRedirect />} />
          <Route path="/guimontgraphics" element={<LegacyRoomRedirect />} />
          <Route path="/juice" element={<LegacyRoomRedirect />} />
          <Route path="/kitchen" element={<LegacyRoomRedirect />} />
          <Route path="/love" element={<LegacyRoomRedirect />} />
          <Route path="/market" element={<LegacyRoomRedirect />} />
          <Route path="/MogulP" element={<LegacyRoomRedirect />} />
          <Route path="/nightrace" element={<LegacyRoomRedirect />} />
          <Route path="/okayhank" element={<LegacyRoomRedirect />} />
          <Route path="/party" element={<LegacyRoomRedirect />} />
          <Route path="/pearls" element={<LegacyRoomRedirect />} />
          <Route path="/rockysroost" element={<LegacyRoomRedirect />} />
          <Route path="/saisocupid" element={<LegacyRoomRedirect />} />
          <Route path="/sini" element={<LegacyRoomRedirect />} />
          <Route path="/souls" element={<LegacyRoomRedirect />} />
          <Route path="/summer" element={<LegacyRoomRedirect />} />
          <Route path="/tiredofus" element={<LegacyRoomRedirect />} />
          <Route path="/zen" element={<LegacyRoomRedirect />} />

          {/* Admin route - protected by Clerk auth + admin role check in AdminPage */}
          <Route path="/avatar/create" element={
            <>
              <SignedIn><AvatarCreatorPage /></SignedIn>
              <SignedOut><RedirectToSignIn redirectUrl="/avatar/create" /></SignedOut>
            </>
          } />
          <Route path="/admin" element={<AdminPage />} />

          {/* Public creator profile */}
          <Route path="/u/:username" element={<CreatorProfilePage />} />

          <Route path="/:roomIdentifier" element={<PlayerPage />} />
          <Route path="/play/:publishedId" element={<PlayerPage isPublished={true} />} />
          {process.env.NODE_ENV === 'development' && (
            <Route path="/error-test" element={<ErrorTestPage />} />
          )}
          {/* Catch-all route for legacy rooms - redirects to static HTML */}
          <Route path="*" element={<LegacyRoomRedirect />} />
        </Routes>
        </Suspense>
        </ChunkErrorBoundary>
      </Box>
      {shouldShowSurvey && (
        <OnboardingSurveyModal isOpen={true} onDismiss={dismissSurvey} />
      )}
    </>
  );
}

export default App;
